import { message, Modal } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  deleteApplication,
  getApplicationsByContestant,
  updateApplicationRound,
} from "../../api/application";
import { loadState } from "../../stores/store";
import { useSettings } from "../../contexts/settingsContext";
import ApplicationTable from "../Applications/applicationsTable";

const ContestantApplicationsModal = ({ contestant, visible, onCancel }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const { state: settings } = useSettings();

  const fetchApplications = useCallback(async () => {
    if (!contestant?._id) return;

    try {
      setLoading(true);

      const idVal = contestant?.id_number;
      const passportVal = contestant?.passport_number;

      const hasValidIdNumber =
        idVal &&
        typeof idVal === "string" &&
        idVal.trim() !== "" &&
        idVal.trim() !== "0" &&
        idVal.trim().toLowerCase() !== "no";

      const idToSend = hasValidIdNumber ? idVal : passportVal;

      const applicationsData = await getApplicationsByContestant(
        contestant._id,
        idToSend
      );

      const filteredApplications = applicationsData.filter(
        (app) => app.round === contestant.round
      );

      setApplications(filteredApplications);
    } catch (error) {
      console.error("Error fetching applications by contestant:", error);
      message.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, [contestant]);

  useEffect(() => {
    if (visible) {
      fetchApplications();
    }
  }, [visible, contestant, settings.selectedRound, fetchApplications]);

  const handleDelete = useCallback(
    async (applicationId) => {
      try {
        await deleteApplication(applicationId);
        message.success("Application deleted successfully");
        fetchApplications();
      } catch (error) {
        message.error("Failed to delete application");
        console.error("Error deleting application:", error);
      }
    },
    [fetchApplications]
  );

  const isAdmin = useMemo(() => loadState().user.role === "admin", []);

  const handleRowClick = useCallback(
    (index) => {
      setSelectedRow(index);
      setSelectedApplication(applications[index]);
    },
    [applications]
  );

  const handleMoveToNextRound = useCallback(
    async (applicationId) => {
      try {
        await updateApplicationRound(applicationId);
        message.success("Application moved to next round");
        fetchApplications();
      } catch (error) {
        message.error("Failed to move application to next round");
        console.error("Error moving application to next round:", error);
      }
    },
    [fetchApplications]
  );

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      title={`Applications for ${contestant.first_name} ${contestant.last_name} - Round ${contestant.round}`}
      width={1000}
      footer={null}
    >
      <ApplicationTable
        loading={loading}
        applications={applications}
        isAdmin={isAdmin}
        onRowClick={handleRowClick}
        onDelete={handleDelete}
        onMoveToNextRound={handleMoveToNextRound}
        onApplicationUpdate={fetchApplications}  // 👈 same idea here
      />
    </Modal>
  );
};

export default ContestantApplicationsModal;